import moment from "moment";
import { formatPhoneNumber } from "../../utils/phoneNumberFormatter";
import { eventEHRCustomToastMessage } from "../../component-lib/Atoms/custom-toast-message/EventEHRCustomToastMessage";

// This function is reusable when we need to send same type of patient profile data in query params to other route we can use this route
export const setPatientDetailsQueryParamsData = (row: any) => {
  const data = {
    firstName: row?.firstName,
    lastName: row?.lastName,
    email: row?.email,
    profilePicture: row?.profilePicture,
    contactNumber: row?.contactNumber,
    bsaId: row?.bsaId,
    dob: row?.dob,
    jamboreeUnitNumber: row?.jamboreeUnitNumber,
    jamboreeUnitLeaderName: row?.jamboreeUnitLeaderName,
  };
  const serializedData = encodeURIComponent(JSON.stringify(data));
  return serializedData;
};

export const getPatientDetailsDataFunction = (patientData: any) => {
  return [
    {
      userProfileImage: patientData?.imgUrl,

      patientData: [
        {
          label: "Full Name",
          value:
            `${patientData?.patient?.participant?.firstName || "-"} ${patientData?.patient?.participant?.lastName || ""}` ||
            "-",
        },
        {
          label: "BSA ID",
          value: patientData?.patient?.participant?.bsaId || "-",
        },
        {
          label: "Email",
          value: patientData?.patient?.participant?.email || "-",
        },
        {
          label: "Contact Number",
          value: patientData?.patient?.participant?.contactNumber || "-",
        },
        {
          label: "Emergency Contact Number",
          value: patientData?.patient?.participant?.emergencyContact?.[0]
            ?.contactNumber
            ? formatPhoneNumber(
                patientData?.patient?.participant?.emergencyContact?.[0]
                  ?.contactNumber
              )
            : "-",
        },
        {
          label: "Incident Date",
          value: patientData?.incidentDate
            ? // Date-only value (midnight UTC) — render in UTC so it never shifts.
              moment.utc(patientData?.incidentDate).format("MM/DD/YYYY")
            : "-",
        },
        {
          label: "Incident Time",
          value: patientData?.incidentTime ? patientData?.incidentTime : "-",
        },
        {
          label: "Witness Name",
          value: patientData?.witnessName ? patientData?.witnessName : "-",
        },
        {
          label: "Incident Location",
          value: patientData?.incidentlocation
            ? patientData?.incidentlocation
            : "-",
        },
      ],
    },
  ];
};

// export const getTriageDetailsDataFunction = (triageData: any) => {
//   return triageData?.map((data: any, index: any) => [
//     {
//       label:
//         data?.temperature === null
//           ? "-"
//           : data?.temperature < 97.0
//             ? "Low"
//             : data?.temperature > 99.0
//               ? "High"
//               : "Moderate",
//       temperature: data?.temperature?.toFixed(2) || "0.00",
//       title: "Temperature",
//       unit: "°F",
//     },
//     {
//       label:
//         data?.oxygenSaturation === null
//           ? "-"
//           : data?.oxygenSaturation < 95.0
//             ? "Low"
//             : "Moderate",
//       temperature: data?.oxygenSaturation?.toFixed(2) || "0.00",
//       title: "Oxygen Saturation",
//       unit: "%",
//     },
//     {
//       label:
//         data?.heartRate === null
//           ? "-"
//           : data?.heartRate < 60
//             ? "Low"
//             : data?.heartRate > 100
//               ? "High"
//               : "Moderate",
//       temperature: data?.heartRate?.toFixed(2) || "0.00",
//       title: "Heart Rate",
//       unit: "bpm",
//     },
//     {
//       label:
//         data?.respirationRate === null
//           ? "-"
//           : data?.respirationRate < 12
//             ? "Low"
//             : data?.respirationRate > 20
//               ? "High"
//               : "Moderate",
//       temperature: data?.respirationRate?.toFixed(2) || "0.00",
//       title: "Respiration Rate",
//       unit: "breath per minute",
//     },
//     {
//       label:
//         data?.systolic === null || data?.diastolic === null
//           ? "-"
//           : data?.systolic < 90 || data?.diastolic < 60
//             ? "Low"
//             : data?.systolic > 120 || data?.diastolic > 80
//               ? "High"
//               : "Moderate",
//       temperature: `${data?.systolic?.toFixed(2)}/${data?.diastolic?.toFixed(2)}` || "0.00",
//       title: "Blood Pressure",
//       unit: "mmhg",
//     },
//   ]);
// };

export const getTriageDetailsDataFunction = (triageData: any) => {
  // Temperature keeps one decimal; the other vitals are whole numbers, so we
  // strip trailing zeros (120.00 -> 120) via Number() rather than forcing decimals.
  const whole = (v: number | null | undefined) =>
    v === null || v === undefined ? "0" : `${Number(v)}`;
  return triageData?.map((data: any, index: any) => [
    {
      label:
        data?.temperature === null
          ? "-"
          : data?.temperature < 97.0
            ? "Low"
            : data?.temperature > 99.0
              ? "High"
              : "Moderate",
      temperature: data?.temperature?.toFixed(1) || "0.0",
      title: "Temperature",
      unit: "°F",
    },
    {
      label:
        data?.oxygenSaturation === null
          ? "-"
          : data?.oxygenSaturation < 95.0
            ? "Low"
            : "Moderate",
      temperature: whole(data?.oxygenSaturation),
      title: "Oxygen Saturation",
      unit: "%",
    },
    {
      label:
        data?.heartRate === null
          ? "-"
          : data?.heartRate < 60
            ? "Low"
            : data?.heartRate > 100
              ? "High"
              : "Moderate",
      temperature: whole(data?.heartRate),
      title: "Heart Rate",
      unit: "bpm",
    },
    {
      label:
        data?.respirationRate === null
          ? "-"
          : data?.respirationRate < 12
            ? "Low"
            : data?.respirationRate > 20
              ? "High"
              : "Moderate",
      temperature: whole(data?.respirationRate),
      title: "Respiration Rate",
      unit: "breath per minute",
    },
    {
      label:
        data?.systolic === null || data?.diastolic === null
          ? "-"
          : data?.systolic > 180 || data?.diastolic > 120
            ? "Hypertension Crisis"
            : data?.systolic >= 140 || data?.diastolic >= 90
              ? "Stage 2 Hypertension"
              : (data?.systolic >= 130 && data?.systolic <= 139) ||
                  (data?.diastolic >= 80 && data?.diastolic <= 89)
                ? "Stage 1 Hypertension"
                : data?.systolic >= 120 &&
                    data?.systolic <= 129 &&
                    data?.diastolic >= 60 &&
                    data?.diastolic <= 80
                  ? "Elevated"
                  : data?.systolic >= 90 &&
                      data?.systolic < 120 &&
                      data?.diastolic >= 60 &&
                      data?.diastolic < 80
                    ? "Moderate"
                    : data?.systolic < 90 && data?.diastolic < 60
                      ? "Low"
                      : data?.diastolic > 120 // Additional conditions of diastolic wnenerver there is complex combinations of SYS & DIA
                        ? "Hypertension Crisis"
                        : data?.diastolic >= 90 && data?.diastolic <= 120
                          ? "Stage 2 Hypertension"
                          : data?.diastolic >= 80 && data?.diastolic <= 89
                            ? "Stage 1 Hypertension"
                            : data?.diastolic >= 60 && data?.diastolic < 80
                              ? "Moderate"
                              : data?.diastolic < 60
                                ? "Low"
                                : "Invalid Data",

      temperature:
        data?.systolic && data?.diastolic
          ? `${Number(data?.systolic)}/${Number(data?.diastolic)}`
          : "0/0",
      title: "Blood Pressure",
      unit: "mmhg",
    },
  ]);
};

export const filterMasterCodeData = (codes: any) => {
  return codes?.map((data: any, index: number) => {
    // Always show the `description` in the label; fall back to `diagnosisName`
    // only when a code has no description (the dropdown filters on the label).
    const diagnosisText = data?.description || data?.diagnosisName;
    return {
      index: index,
      label: diagnosisText ? `${data?.code} - ${diagnosisText}` : data?.code,
      value: data?.code,
      description: data?.description,
      id: data?.id,
      type: data?.type,
      diagnosisName: data?.diagnosisName,
    };
  });
};

export const getDefaultSelectedCare = (defaultSelectedTriageData: any) => {
  return defaultSelectedTriageData?.woundCare
    ? "woundCare"
    : defaultSelectedTriageData?.dehydration
      ? "dehydration"
      : defaultSelectedTriageData?.injury
        ? "injury"
        : defaultSelectedTriageData?.other
          ? "other"
          : "woundCare"; // Default value
};

export const getDefaultSelectedCareValue = (defaultSelectedTriageData: any) => {
  return defaultSelectedTriageData?.woundCare
    ? defaultSelectedTriageData?.woundlocation
    : defaultSelectedTriageData?.dehydration
      ? defaultSelectedTriageData?.dehydrationLevel
      : defaultSelectedTriageData?.injury
        ? defaultSelectedTriageData?.injurylocation
        : defaultSelectedTriageData?.other
          ? defaultSelectedTriageData?.details
          : defaultSelectedTriageData?.woundlocation; // Default value
};

export const getSelectedCareTitle = (selectedCare: any) => {
  return selectedCare === "woundCare"
    ? "Wound Severity"
    : selectedCare === "other"
      ? "Details"
      : selectedCare === "dehydration"
        ? "Hydration Level"
        : selectedCare === "injury"
          ? "Injury Location"
          : ""; // Default value
};

export const getSelectedCarePlaceholder = (selectedCare: any) => {
  return selectedCare === "woundCare"
    ? "Enter Wound Severity"
    : selectedCare === "other"
      ? "Enter Details"
      : selectedCare === "dehydration"
        ? "Hydration Level"
        : selectedCare === "injury"
          ? "Injury Location"
          : ""; // Default value
};

export const TRIAGE_CARES = [
  { label: "Wound Care", value: "woundCare" },
  { label: "Dehydration", value: "dehydration" },
  { label: "Injury", value: "injury" },
  { label: "Other", value: "other" },
];

export const decideStatusOnButtonClick = (visitNoteCareFlowData: any) => {
  return "InDisposition";
};

export const setVisitNoteInfoDisable = (status: string) => {
  return (
    status === "transferOffProperty" ||
    status === "transferToClinic" ||
    status === "transferToJHC" ||
    status === "discharged"
  );
};

export const JOT_FORM_DATA = {
  DATE_OF_ENCOUNTER: "dateOfEncounter",
  JAMBOREE_INCIDENT_NUMBER: "jamboreeIncidentNumber",
  TIME_OF_ENCOUNTER: "timeOfEncounter",
  UNIT_TYPE: "unitType",
  UNIT_DESIGNATION: "unitDesignation",
  PATIENT_NAME: "patientName",
  DOB: "dateOFBirth",
  AGE: "Age",
  PHONE_NUMBER: "phoneNumber",
  GENDER: "gender",
  OTHER: "other",
  VISITOR: "visitor",
  REGISTRATION_NUMBER: "registrationNumber",
  COUNCIL_NAME: "councilName",
  STAFF_ASSIGNMENT: "staffAssignment",
  JUN: "jun",
  INCIDENT_LOCATION: "incidentLocation",
  INCIDENT_DESCRIPTION: "incidentDescription",
  CHIEF_COMPLAINT: "chiefComplaint",
  WITNESS_NAME: "witnessName",
  WITNESS_PHONE_NUMBER: "witnessPhoneNumber",
  SUBJECTIVE: "subjective",
  OBJECTIVE: "objective",
  ASSESSMENT_AND_PLAN: "assessmentAndPlan",
  LEAD_INTERPRETATION_TEXT: "leadInterpretation",
  IMPRESSION_DIAGNOSIS: "impressionDiagnosis",
  DISPOSITION_TYPE: "dispositionType",
  TRANSFERRING_UNIT: "transferringUnit",
  TRANSPORT_DESTINATION_TYPE: "transportDestinationType",
  TYPE_OF_CARE: "typeOfCare",
  TYPE_OF_CARE_NAME: "typeOfCareName",
  TREAT_AND_RELEASE_CHECKED: "treatAndReleaseChecked",
  TREAT_AND_RELEASE_USER_NAME: "treatAndReleaseUserName",
  REFUSAL_OF_MEDICAL_AID_NAME: "RefusalOfMedicalAidName",
  REFUSAL_OF_MEDICAL_AID_WITNESS_NAME: "RefusalOfMedicalAidWitnessName",
  HOSPITAL_OFF_SITE_FACILITY: "hospitalOffSiteFacility",
  HOSPITAL_OFF_SITE_FACILITY_TRANSFERRING_UNIT:
    "hospitalOffSiteTransferringUnit",
  FINAL_SIGNATURE_TIME: "finalSignatureTime",
  FINAL_PRIMARY_TREATING_CLINICIAN: "finalPrimaryTreatingClinician",
  FINAL_SIGNATURE_NAME: "finalSignatureName",
  IFT_SOAP_NOTE: "iftSoapNote",
};

export const getSortedFormData = (data: any) => {
  const validationChecks = [
    { key: "unitType", message: "Please select unit type" },
    { key: "visitor", message: "Please select staff, participant or visitor" },
    { key: "gender", message: "Please select gender" },
  ];

  for (const { key, message } of validationChecks) {
    if (!data?.[key]) {
      eventEHRCustomToastMessage({ title: message, status: "error" });
      return "error";
    }
  }

  if (data?.unitType === "jeu_bls" || data?.unitType === "jeu_als") {
    const validationChecks = [
      { key: "incidentLocation", message: "Please select incident location" },
      {
        key: "incidentDescription",
        message: "Please provide BRIEF factual description of incident.",
      },
    ];

    for (const { key, message } of validationChecks) {
      if (!data?.[key]) {
        eventEHRCustomToastMessage({ title: message, status: "error" });
        return "error";
      }
    }
  }

  const { dbp, pulse, respRate, sbp, spo2 } = data?.vitals?.[0];

  if (data?.unitType !== "mf_ito") {
    if (!data?.subjective) {
      eventEHRCustomToastMessage({
        title: "Subjective is required",
        status: "error",
      });
      return "error";
    }
    if (
      !(
        pulse?.trim()?.length &&
        respRate?.trim()?.length &&
        dbp?.trim()?.length &&
        sbp?.trim()?.length &&
        spo2?.trim()?.length
      )
    ) {
      eventEHRCustomToastMessage({
        title: "Vital Signs are required",
        status: "error",
      });
      return "error";
    }
  }

  if (!data?.dispositionType) {
    eventEHRCustomToastMessage({
      title: "Please select disposition",
      status: "error",
    });
    return "error";
  }

  if (
    data?.dispositionType === "treated_and_transported" &&
    data?.transportDestinationType === ""
  ) {
    // if (!data?.[key]) {
    eventEHRCustomToastMessage({
      title: "Please select transport destination",
      status: "error",
    });
    return "error";
    // }
  }

  if (
    data?.dispositionType === "treated_and_transported" &&
    !data?.typeOfCare
  ) {
    eventEHRCustomToastMessage({
      title: "Please select type of care",
      status: "error",
    });
    return "error";
  }
  const isValidTimeFormat = (time: string) => {
    return time ? /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(time) : true;
  };

  return {
    // done
    encounter: {
      incidentDate: data?.dateOfEncounter
        ? moment(data?.dateOfEncounter).format("YYYY-MM-DD")
        : undefined,
      incidentNumber: data?.jamboreeIncidentNumber || undefined,
      incidentTime: !isValidTimeFormat(data?.timeOfEncounter)
        ? data?.timeOfEncounter?.format("HH:mm")
        : data?.timeOfEncounter,

      unitType:
        data?.unitType === "jeu_bls"
          ? "Jamboree EMS UTV-BLS"
          : data?.unitType === "jeu_als"
            ? "Jamboree EMS UTV-ALS"
            : data?.unitType === "mf_ito"
              ? "Military FLA-Interfacility Transport_ONLY"
              : "",
      unitDesignation: data?.unitDesignation || undefined,
    },

    // done
    demographic: {
      participantType:
        data?.visitor === "jst"
          ? "JST"
          : data?.visitor === "participant"
            ? "Participant"
            : data?.visitor === "visitor"
              ? "Visitor"
              : data?.visitor === "exhibitor"
                ? "Exhibitor"
                : data?.visitor === "summitStaff"
                  ? "Summit Staff"
                  : "",
      participantId: data?.patientName,
      gender: data?.gender,
      age: data?.Age ? Number(data?.Age) : "",
      dateOfBirth: data?.dateOFBirth
        ? moment(data?.dateOFBirth).format("YYYY-MM-DD")
        : "",
      contactNumber: data?.phoneNumber || undefined,
      registrationId: data?.registrationNumber,
      jamboreeUnitNumber: data?.jun || undefined,
      councilName: data?.councilName || undefined,
      staffAssignment: data?.staffAssignment || undefined,
    },

    // done
    incidentDetails:
      data?.unitType !== "mf_ito"
        ? {
            incidentlocation: data?.incidentLocation,
            chiefComplaints: data?.incidentDescription,
            witnessName: data?.witnessName || undefined,
            witnessContact: data?.witnessPhoneNumber || undefined,
          }
        : undefined,

    // done
    soapDetails:
      data?.unitType !== "mf_ito"
        ? {
            chiefComplaints: data?.chiefComplaint || undefined,
            subjectiveNote: data?.subjective || undefined,
            objectiveNote: data?.objective || undefined,
            assessmentDescription: data?.assessmentAndPlan || undefined,
            vitals: data?.vitals?.map((data: any) => ({
              pulse: data?.pulse ? Number(data?.pulse) : null,
              SBP: data?.gcs ? Number(data?.gcs) : null,
              DBP: data?.dbp ? Number(data?.dbp) : null,
              SPO2: data?.spo2 ? Number(data?.spo2) : null,
              GCS: data?.gcs ? Number(data?.gcs) : null,
              time: !isValidTimeFormat(data?.time)
                ? data?.time?.format("HH:mm")
                : data?.time,
              respirationRate: data?.respRate ? Number(data?.respRate) : null,
            })),
            procedureDone: {
              airwayManagement: {
                select: data?.procedureDone?.airwayManagement,
                bvm: data?.procedureDone?.airwayManagement
                  ? data?.airwayManagement?.bvm
                  : undefined,
                suctioning: data?.procedureDone?.airwayManagement
                  ? data?.airwayManagement?.suctioning
                  : undefined,
                OPA: data?.procedureDone?.airwayManagement
                  ? data?.airwayManagement?.oPA
                  : undefined,
                NPA: data?.procedureDone?.airwayManagement
                  ? data?.airwayManagement?.nPA
                  : undefined,
                iCGEL:
                  data?.procedureDone?.airwayManagement &&
                  data?.airwayManagement?.iGel
                    ? {
                        select: data?.airwayManagement?.iGel,
                        size: data?.iGelProperties?.size,
                        attempts: data?.iGelProperties?.attempts,
                        successful: data?.iGelProperties?.successful
                          ? true
                          : false,
                        time: !isValidTimeFormat(data?.iGelProperties?.time)
                          ? data?.iGelProperties?.time?.format("HH:mm")
                          : data?.iGelProperties?.time,
                      }
                    : undefined,
                endotrachealIntubation:
                  data?.procedureDone?.airwayManagement &&
                  data?.airwayManagement?.endotrachealIntubation
                    ? {
                        select: data?.airwayManagement?.endotrachealIntubation,
                        device: data?.endotrachealIntubationProperties?.device,
                        bladeSize:
                          data?.endotrachealIntubationProperties?.bladeSize,
                        attempts:
                          data?.endotrachealIntubationProperties?.attempts,
                        tubeSize:
                          data?.endotrachealIntubationProperties?.tubeSize,
                        successful: data?.endotrachealIntubationProperties
                          ?.successful
                          ? true
                          : false,
                        time: !isValidTimeFormat(
                          data?.endotrachealIntubationProperties?.time
                        )
                          ? data?.endotrachealIntubationProperties?.time?.format(
                              "HH:mm"
                            )
                          : data?.endotrachealIntubationProperties?.time,
                      }
                    : undefined,
                other:
                  data?.procedureDone?.airwayManagement &&
                  data?.airwayManagement?.other
                    ? {
                        select: data?.airwayManagement?.other,
                        desription: data?.airwayManagement?.otherOptionValue,
                      }
                    : undefined,
              },
              woundCare_dressing: data?.procedureDone?.woundCareDressing
                ? {
                    select: data?.procedureDone?.woundCareDressing,
                  }
                : undefined,
              IVPlacement: data?.procedureDone?.iVPlacement
                ? [
                    {
                      select: data?.procedureDone?.iVPlacement,
                      data: data?.ivInsertionData?.map((data1: any) => ({
                        select: data?.procedureDone?.iVPlacement,
                        size: data1?.size,
                        attempts: data1?.attempts,
                        successful: data1?.successful,
                        time: !isValidTimeFormat(data1?.time)
                          ? data1?.time?.format("HH:mm")
                          : data1?.time,
                      })),
                    },
                  ]
                : undefined,
              MedicationAdministered: data?.procedureDone
                ?.medicationsAdministered
                ? [
                    {
                      select: data?.procedureDone?.medicationsAdministered,
                      meds: data?.medicationsAdministeredData?.map(
                        (data: any) => ({
                          medication: data?.medication,
                          dose: data?.dose,
                          route: {
                            IV: data?.route === "IV" ? true : false,
                            IM: data?.route === "IM" ? true : false,
                            PQ: data?.route === "PO" ? true : false,
                            SQ: data?.route === "SQ" ? true : false,
                          },
                          response: {
                            Improve:
                              data?.response === "Improve" ? true : false,
                            nochange:
                              data?.response === "No change" ? true : false,
                            worsen: data?.response === "Wornsen" ? true : false,
                            time: !isValidTimeFormat(data?.time)
                              ? data?.time?.format("HH:mm")
                              : data?.time,
                          },
                        })
                      ),
                    },
                  ]
                : undefined,

              Lead12EKG: data?.procedureDone?.LeadEKG
                ? {
                    select: data?.procedureDone?.LeadEKG,
                    lead12Interpration: data?.leadInterpretation,
                    lead12photo: data?.leadPhoto,
                  }
                : undefined,

              Splinting: data?.procedureDone?.splinting
                ? {
                    select: data?.procedureDone?.splinting,
                  }
                : undefined,
              Oxygen: data?.procedureDone?.oxygen
                ? [
                    {
                      select: data?.procedureDone?.oxygen,
                      IVs: data?.oxygenAdministeredData?.map((data: any) => ({
                        device: data?.device,
                        LPM: data?.lpm,
                        time: !isValidTimeFormat(data?.time)
                          ? data?.time?.format("HH:mm")
                          : data?.time,
                        improvement: data?.improvement,
                      })),
                    },
                  ]
                : undefined,
              Other: data?.procedureDone?.other
                ? {
                    select: data?.procedureDone?.other,
                    description: data?.procedureDone?.otherValue,
                  }
                : undefined,
            },
          }
        : undefined,

    // done
    dispositons: {
      transferringUnit: data?.transferringUnit || undefined,
      // Backend expects an array of ICD/DSM codes. The field is single-select,
      // so wrap the one selected code; omit entirely when nothing is selected.
      impressionDiagnosis: data?.impressionDiagnosis
        ? [data.impressionDiagnosis]
        : undefined,
      dispositions: {
        treatedAndTransport:
          data?.dispositionType === "treated_and_transported"
            ? {
                select:
                  data?.dispositionType === "treated_and_transported"
                    ? true
                    : false,
                facilityId: data?.transportDestinationType,
                typeOfCare: data?.typeOfCare || undefined,
                typeOfCareName: data?.typeOfCareName || undefined,
              }
            : undefined,
        refusalOfMedicalAid:
          data?.dispositionType === "refusal_medical_aid_transport"
            ? true
            : false,
        sentToHospitalByEMS:
          data?.dispositionType === "sent_hospital_ems" ? true : false,
        sentToHospitalByAir:
          data?.dispositionType === "sent_hospital_air" ? true : false,
        sentToHospitalByPOV:
          data?.dispositionType === "sent_hospital_pov" ? true : false,
        sentToHospitalByNSJTransportation:
          data?.dispositionType === "sent_hospital_nsj" ? true : false,
        treatedAndReleased:
          data?.dispositionType === "treated_and_released"
            ? {
                select:
                  data?.dispositionType === "treated_and_released"
                    ? true
                    : false,
                radioButton: data?.treatAndReleaseChecked || false,
                name: data?.treatAndReleaseUserName || undefined,
                signature: data?.treatAndReleaseUserName || undefined,
              }
            : undefined,
      },
    },

    // done
    refusalOfMedicalAid:
      data?.dispositionType === "refusal_medical_aid_transport"
        ? {
            name: data?.RefusalOfMedicalAidName || undefined,
            rmaRelease: data?.RefusalOfMedicalAidName || undefined,
            witnessName: data?.RefusalOfMedicalAidWitnessName || undefined,
            witnessSignature: "",
          }
        : undefined,

    // done
    signature: {
      timeCallEnd: !isValidTimeFormat(data?.finalSignatureTime)
        ? data?.finalSignatureTime?.format("HH:mm")
        : data?.finalSignatureTime,
      primaryClinic: data?.finalPrimaryTreatingClinician,
      signature: data?.finalSignatureName,
    },

    // done
    iftSoapNote: data?.unitType === "mf_ito" ? data?.iftSoapNote : undefined,

    // done
    OffSiteTransfer:
      data?.dispositionType === "sent_hospital_ems" ||
      data?.dispositionType === "sent_hospital_air" ||
      data?.dispositionType === "sent_hospital_pov" ||
      data?.dispositionType === "sent_hospital_nsj"
        ? {
            hospitalName: data?.hospitalOffSiteFacility,
            transferringUnit: data?.hospitalOffSiteTransferringUnit,
          }
        : undefined,
  };
};

export const UNIT_DESIGNATION = [
  { label: "Please Select", value: "" },
  { label: "UTV 611", value: "UTV 611" },
  { label: "UTV 612", value: "UTV 612" },
  { label: "UTV 613", value: "UTV 613" },
  { label: "UTV 614", value: "UTV 614" },
  { label: "UTV 615", value: "UTV 615" },
  { label: "MedBed 621", value: "MedBed 621" },
  { label: "MedBed 622", value: "MedBed 622" },
  { label: "MedBed 623", value: "MedBed 623" },
  { label: "MedBed 624", value: "MedBed 624" },
  { label: "MedBed 625", value: "MedBed 625" },
  { label: "FLA 770", value: "FLA 770" },
  { label: "FLA 771", value: "FLA 771" },
  { label: "FLA 772", value: "FLA 772" },
  { label: "FLA 773", value: "FLA 773" },
  { label: "FLA 774", value: "FLA 774" },
  { label: "FLA 775", value: "FLA 775" },
  { label: "FLA 776", value: "FLA 776" },
  { label: "FLA 777", value: "FLA 777" },
  { label: "FLA 778", value: "FLA 778" },
  { label: "FLA 779", value: "FLA 779" },
  { label: "FLA 780", value: "FLA 780" },
  { label: "FLA 781", value: "FLA 781" },
  { label: "FLA 782", value: "FLA 782" },
  { label: "FLA 783", value: "FLA 783" },
  { label: "FLA 784", value: "FLA 784" },
  { label: "FLA 785", value: "FLA 785" },
  { label: "FLA 786", value: "FLA 786" },
  { label: "FLA 787", value: "FLA 787" },
  { label: "FLA 788", value: "FLA 788" },
  { label: "FLA 789", value: "FLA 789" },
  { label: "MD1", value: "MD1" },
  { label: "MD2", value: "MD2" },
  { label: "MD3", value: "MD3" },
  { label: "MEDIC 631", value: "MEDIC 631" },
  { label: "MEDIC 632", value: "MEDIC 632" },
  { label: "MEDIC 633", value: "MEDIC 633" },
  { label: "BIKE 651", value: "BIKE 651" },
  { label: "BIKE 652", value: "BIKE 652" },
  { label: "BIKE 653", value: "BIKE 653" },
  { label: "601", value: "601" },
  { label: "602", value: "602" },
  { label: "603", value: "603" },
  { label: "604", value: "604" },
  { label: "605", value: "605" },
  { label: "606", value: "606" },
];

export const COUNCIL_NAME_LIST = [
  { label: "Please Select", value: "" },
  { label: "Abraham Lincoln Council", value: "abrahamLincolnCouncil" },
  { label: "Adventure West Council", value: "adventureWestCouncil" },
  { label: "Alabama-Florida Council", value: "alabamaFloridaCouncil" },
  { label: "Alamo Area Council", value: "alamoAreaCouncil" },
  { label: "Allegheny Highlands Council", value: "alleghenyHighlandsCouncil" },
  { label: "Aloha Council", value: "alohaCouncil" },
  { label: "Andrew Jackson Council", value: "andrewJacksonCouncil" },
  { label: "Anthony Wayne Area Council", value: "anthonyWayneAreaCouncil" },
  { label: "Arbuckle Area Council", value: "arbuckleAreaCouncil" },
  { label: "Atlanta Area Council", value: "atlantaAreaCouncil" },
  { label: "Baden-Powell Council", value: "badenPowellCouncil" },
  { label: "Baltimore Area Council", value: "baltimoreAreaCouncil" },
  { label: "Bay Area Council", value: "bayAreaCouncil" },
  { label: "Bay Lakes Council", value: "bayLakesCouncil" },
  { label: "Black Hills Area Council", value: "blackHillsAreaCouncil" },
  { label: "Black Swamp Council", value: "blackSwampCouncil" },
  { label: "Black Warrior Council", value: "blackWarriorCouncil" },
  { label: "Blackhawk Area Council", value: "blackhawkAreaCouncil" },
  { label: "Blue Grass Council", value: "blueGrassCouncil" },
  { label: "Blue Mountain Council", value: "Blue Mountain Council" },
  { label: "Blue Ridge Council", value: "Blue Ridge Council" },
  {
    label: "Blue Ridge Mountains Council",
    value: "Blue Ridge Mountains Council",
  },
  { label: "Buckeye Council", value: "Buckeye Council" },
  { label: "Buckskin Council", value: "Buckskin Council" },
  { label: "Bucktail Council", value: "Bucktail Council" },
  { label: "Buffalo Trace Council", value: "Buffalo Trace Council" },
  { label: "Buffalo Trail Council", value: "Buffalo Trail Council" },
  { label: "Caddo Area Council", value: "Caddo Area Council" },
  { label: "Calcasieu Area Council", value: "Calcasieu Area Council" },
  {
    label: "California Inland Empire Co",
    value: "California Inland Empire Co",
  },
  {
    label: "Cape Cod and the Islands Cncl",
    value: "Cape Cod and the Islands Cncl",
  },
  { label: "Cape Fear Council", value: "Cape Fear Council" },
  { label: "Capitol Area", value: "Capitol Area" },
  { label: "Cascade Pacific Council", value: "Cascade Pacific Council" },
  { label: "Catalina Council", value: "Catalina Council" },
  { label: "Central Florida Council", value: "Central Florida Council" },
  { label: "Central Georgia Council", value: "Central Georgia Council" },
  { label: "Central Minnesota Council", value: "Central Minnesota Council" },
  {
    label: "Central North Carolina Coun",
    value: "Central North Carolina Coun",
  },
  { label: "Chattahoochee Council", value: "Chattahoochee Council" },
  { label: "Cherokee Area Council", value: "Cherokee Area Council" },
  { label: "Cherokee Area Council", value: "Cherokee Area Council" },
  { label: "Chester County Council", value: "Chester County Council" },
  { label: "Chickasaw Council", value: "Chickasaw Council" },
  { label: "Chief Cornplanter Council", value: "Chief Cornplanter Council" },
  { label: "Chief Seattle Council", value: "Chief Seattle Council" },
  { label: "Chippewa Valley Council", value: "Chippewa Valley Council" },
  { label: "Choctaw Area Council", value: "Choctaw Area Council" },
  { label: "Cimarron Council", value: "Cimarron Council" },
  { label: "Circle Ten Council", value: "Circle Ten Council" },
  { label: "Coastal Carolina Council", value: "Coastal Carolina Council" },
  { label: "Coastal Georgia Council", value: "Coastal Georgia Council" },
  { label: "Colonial Virginia Council", value: "Colonial Virginia Council" },
  { label: "Columbia-Montour Council", value: "Columbia-Montour Council" },
  { label: "Connecticut Rivers Council", value: "Connecticut Rivers Council" },
  { label: "Connecticut Yankee Council", value: "Connecticut Yankee Council" },
  { label: "Conquistador Council", value: "Conquistador Council" },
  { label: "Cornhusker Council", value: "Cornhusker Council" },
  { label: "Coronado Area Council", value: "Coronado Area Council" },
  { label: "Cradle of Liberty Council", value: "cradle_of_liberty_council" },
  { label: "Crater Lake Council", value: "crater_lake_council" },
  {
    label: "Crossroads of America Council",
    value: "crossroads_of_america_council",
  },
  { label: "Crossroads of the West", value: "crossroads_of_the_west" },
  { label: "Dan Beard Council", value: "dan_beard_council" },
  { label: "Daniel Boone Council", value: "daniel_boone_council" },
  { label: "Daniel Webster Council", value: "daniel_webster_council" },
  { label: "Del-Mar-Va Council", value: "del_mar_va_council" },
  { label: "DeSoto Area Council", value: "desoto_area_council" },
  { label: "East Carolina Council", value: "east_carolina_council" },
  { label: "East Texas Area Council", value: "east_texas_area_council" },
  { label: "Erie Shores Council", value: "erie_shores_council" },
  { label: "Evangeline Area Council", value: "evangeline_area_council" },
  { label: "Far East Council", value: "far_east_council" },
  { label: "Five Rivers Council", value: "five_rivers_council" },
  { label: "Flint River Council", value: "flint_river_council" },
  { label: "French Creek Council", value: "french_creek_council" },
  { label: "Gamehaven Council", value: "gamehaven_council" },
  { label: "Garden State Council", value: "garden_state_council" },
  { label: "Gateway Area Council", value: "gateway_area_council" },
  { label: "Georgia-Carolina Council", value: "Georgia-Carolina Council" },
  { label: "Glacier's Edge Council", value: "Glacier's Edge Council" },
  { label: "Golden Empire Council", value: "Golden Empire Council" },
  { label: "Golden Gate Area Council", value: "Golden Gate Area Council" },
  { label: "Golden Spread Council", value: "Golden Spread Council" },
  { label: "Grand Canyon Council", value: "Grand Canyon Council" },
  { label: "Grand Teton Council", value: "Grand Teton Council" },
  { label: "Great Alaska Council", value: "Great Alaska Council" },
  { label: "Great Rivers Council", value: "Great Rivers Council" },
  {
    label: "Great Smoky Mountain Council",
    value: "Great Smoky Mountain Council",
  },
  { label: "Great Southwest Council", value: "Great Southwest Council" },
  { label: "Great Trail Council", value: "Great Trail Council" },
  { label: "Greater Alabama Council", value: "Greater Alabama Council" },
  { label: "Greater Colorado Council", value: "Greater Colorado Council" },
  {
    label: "Greater Hudson Valley Council",
    value: "Greater Hudson Valley Council",
  },
  { label: "Greater LA Area Council", value: "Greater LA Area Council" },
  { label: "Greater New York Council", value: "Greater New York Council" },
  {
    label: "Greater Niagara Frontier Co",
    value: "Greater Niagara Frontier Co",
  },
  {
    label: "Greater St Louis Area Council",
    value: "Greater St Louis Area Council",
  },
  {
    label: "Greater Tampa Bay Area Cncl",
    value: "Greater Tampa Bay Area Cncl",
  },
  { label: "Greater Yosemite Council", value: "Greater Yosemite Council" },
  { label: "Green Mountain Council", value: "Green Mountain Council" },
  { label: "Greenwich Council", value: "Greenwich Council" },
  { label: "Gulf Coast Council", value: "Gulf Coast Council" },
  { label: "Gulf Stream Council", value: "Gulf Stream Council" },
  { label: "Hawk Mountain Council", value: "Hawk Mountain Council" },
  { label: "Hawkeye Area Council", value: "Hawkeye Area Council" },
  { label: "Heart of America Council", value: "Heart of America Council" },
  {
    label: "Heart of New England Council",
    value: "Heart of New England Council",
  },
  { label: "Heart of Virginia Council", value: "Heart of Virginia Council" },
  { label: "Hoosier Trails Council", value: "Hoosier Trails Council" },
  { label: "Housatonic Council", value: "Housatonic Council" },
  { label: "Illowa Council", value: "Illowa Council" },
  { label: "Indian Nations Council", value: "Indian Nations Council" },
  { label: "Indian Waters Council", value: "Indian Waters Council" },
  { label: "Inland Northwest Council", value: "Inland Northwest Council" },
  { label: "Iroquois Trail Council", value: "Iroquois Trail Council" },
  { label: "Istrouma Area Council", value: "Istrouma Area Council" },
  { label: "Jayhawk Area Council", value: "Jayhawk Area Council" },
  { label: "Jersey Shore Council", value: "Jersey Shore Council" },
  { label: "Juniata Valley Council", value: "Juniata Valley Council" },
  { label: "Katahdin Area Council", value: "Katahdin Area Council" },
  { label: "La Salle Council", value: "La Salle Council" },
  { label: "Lake Erie Council", value: "Lake Erie Council" },
  { label: "Las Vegas Area Council", value: "Las Vegas Area Council" },
  { label: "Last Frontier Council", value: "Last Frontier Council" },
  { label: "Laurel Highlands Council", value: "Laurel Highlands Council" },
  { label: "Leatherstocking Council", value: "Leatherstocking Council" },
  { label: "Lincoln Heritage Council", value: "Lincoln Heritage Council" },
  { label: "Long Beach Area Council", value: "Long Beach Area Council" },
  { label: "Longhorn Council", value: "Longhorn Council" },
  { label: "Longhouse Council", value: "Longhouse Council" },
  { label: "Los Padres Council", value: "Los Padres Council" },
  { label: "Louisiana Purchase Council", value: "Louisiana Purchase Council" },
  { label: "Marin Council", value: "Marin Council" },
  { label: "Mason-Dixon Council", value: "Mason-Dixon Council" },
  { label: "Mayflower Council", value: "Mayflower Council" },
  { label: "Mecklenburg County Council", value: "Mecklenburg County Council" },
  { label: "Miami Valley Council", value: "Miami Valley Council" },
  {
    label: "Michigan Crossroads Council",
    value: "Michigan Crossroads Council",
  },
  { label: "North Florida Council", value: "North Florida Council" },
  { label: "Northeast Georgia Council", value: "Northeast Georgia Council" },
  { label: "Northeast Illinois Council", value: "Northeast Illinois Council" },
  { label: "Northeast Iowa Council", value: "Northeast Iowa Council" },
  {
    label: "Northeastern Pennsylvania Council",
    value: "Northeastern Pennsylvania Council",
  },
  { label: "Northern Lights Council", value: "Northern Lights Council" },
  {
    label: "Northern New Jersey Council",
    value: "Northern New Jersey Council",
  },
  { label: "Northern Star Council", value: "Northern Star Council" },
  { label: "Northwest Georgia Council", value: "Northwest Georgia Council" },
  { label: "Northwest Texas Council", value: "Northwest Texas Council" },
  { label: "Norwela Council", value: "Norwela Council" },
  { label: "Occoneechee Council", value: "Occoneechee Council" },
  { label: "Ohio River Valley Council", value: "Ohio River Valley Council" },
  { label: "Old Hickory Council", value: "Old Hickory Council" },
  { label: "Old North State Council", value: "Old North State Council" },
  { label: "Orange County Council", value: "Orange County Council" },
  { label: "Oregon Trail Council", value: "Oregon Trail Council" },
  { label: "Overland Trails Council", value: "Overland Trails Council" },
  { label: "Ozark Trails Council", value: "Ozark Trails Council" },
  { label: "Pacific Harbors Council", value: "Pacific Harbors Council" },
  { label: "Pacific Skyline Council", value: "Pacific Skyline Council" },
  { label: "Palmetto Council", value: "Palmetto Council" },
  {
    label: "Pathway to the Rockies Council",
    value: "Pathway to the Rockies Council",
  },
  { label: "Patriots' Path Council", value: "Patriots' Path Council" },
  { label: "Pennsylvania Dutch Council", value: "Pennsylvania Dutch Council" },
  { label: "Piedmont Council", value: "Piedmont Council" },
  { label: "Piedmont Council", value: "Piedmont Council" },
  { label: "Pine Burr Area Council", value: "Pine Burr Area Council" },
  { label: "Pine Tree Council", value: "Pine Tree Council" },
  { label: "Pony Express Council", value: "Pony Express Council" },
  { label: "Potawatomi Area Council", value: "Potawatomi Area Council" },
  { label: "Prairielands Council", value: "Prairielands Council" },
  { label: "Puerto Rico Council", value: "Puerto Rico Council" },
  { label: "Quapaw Area Council", value: "Quapaw Area Council" },
  { label: "Quivira Council", value: "Quivira Council" },
  { label: "Rainbow Council", value: "Rainbow Council" },
  { label: "Redwood Empire Council", value: "Redwood Empire Council" },
  { label: "Rio Grande Council", value: "Rio Grande Council" },
  { label: "Rip Van Winkle Council", value: "Rip Van Winkle Council" },
  { label: "Sagamore Council", value: "Sagamore Council" },
  { label: "Sam Houston Area Council", value: "Sam Houston Area Council" },
  { label: "Samoset Council", value: "Samoset Council" },
  { label: "San Diego-Imperial Council", value: "San Diego-Imperial Council" },
  { label: "Santa Fe Trail Council", value: "Santa Fe Trail Council" },
  { label: "Seneca Waterways Council", value: "Seneca Waterways Council" },
  { label: "Sequoia Council", value: "Sequoia Council" },
  { label: "Sequoyah Council", value: "Sequoyah Council" },
  { label: "Shenandoah Area Council", value: "Shenandoah Area Council" },
  {
    label: "Silicon Valley Monterey Bay",
    value: "Silicon Valley Monterey Bay",
  },
  { label: "Simon Kenton Council", value: "Simon Kenton Council" },
  { label: "Sioux Council", value: "Sioux Council" },
  { label: "South Florida Council", value: "South Florida Council" },
  { label: "South Georgia Council", value: "South Georgia Council" },
  { label: "South Plains Council", value: "South Plains Council" },
  { label: "South Texas Council", value: "South Texas Council" },
  {
    label: "Southeast Louisiana Council",
    value: "Southeast Louisiana Council",
  },
  { label: "Southern Sierra Council", value: "Southern Sierra Council" },
  { label: "Southwest Florida Council", value: "Southwest Florida Council" },
  {
    label: "Spirit of Adventure Council",
    value: "Spirit of Adventure Council",
  },
  { label: "Suffolk County Council", value: "Suffolk County Council" },
  { label: "Susquehanna Council", value: "Susquehanna Council" },
  {
    label: "Suwannee River Area Council",
    value: "Suwannee River Area Council",
  },
  { label: "Tecumseh Council", value: "Tecumseh Council" },
  { label: "Texas Southwest Council", value: "Texas Southwest Council" },
  { label: "Texas Trails Council", value: "Texas Trails Council" },
  {
    label: "The Pathway to Adventure Council",
    value: "The Pathway to Adventure Council",
  },
  { label: "Theodore Roosevelt Council", value: "Theodore Roosevelt Council" },
  { label: "Three Fires Council", value: "Three Fires Council" },
  { label: "Three Harbors Council", value: "Three Harbors Council" },
  { label: "Three Rivers Council", value: "Three Rivers Council" },
  { label: "Tidewater Council", value: "Tidewater Council" },
  { label: "Transatlantic Council", value: "Transatlantic Council" },
  { label: "Tukabatchee Area Council", value: "Tukabatchee Area Council" },
  { label: "Tuscarora Council", value: "Tuscarora Council" },
  { label: "Twin Rivers Council", value: "Twin Rivers Council" },
  { label: "Twin Valley Council", value: "Twin Valley Council" },
  { label: "Ventura County Council", value: "Ventura County Council" },
  { label: "Verdugo Hills Council", value: "Verdugo Hills Council" },
  {
    label: "Virginia Headwaters Council",
    value: "Virginia Headwaters Council",
  },
  { label: "Voyageurs Area Council", value: "Voyageurs Area Council" },
  { label: "W. D. Boyce Council", value: "W. D. Boyce Council" },
  {
    label: "Washington Crossing Council",
    value: "Washington Crossing Council",
  },
  { label: "West Tennessee Council", value: "West Tennessee Council" },
  { label: "Westark Area Council", value: "Westark Area Council" },
  { label: "Western Los Angeles County", value: "Western Los Angeles County" },
  {
    label: "Western Massachusetts Council",
    value: "Western Massachusetts Council",
  },
  {
    label: "Westmoreland-Fayette Council",
    value: "Westmoreland-Fayette Council",
  },
  { label: "Winnebago Council", value: "Winnebago Council" },
  { label: "Yucca Council", value: "Yucca Council" },
];

export const INCIDENT_LOCATION = [
  { label: "Please Select", value: "" },

  { label: "A Better World", value: "A Better World" },
  { label: "ACME Pioneering", value: "ACME Pioneering" },
  { label: "Action Point BMX", value: "Action Point BMX" },
  { label: "Action Point Canopy Tours", value: "Action Point Canopy Tours" },
  {
    label: "Action Point Challenge Course",
    value: "Action Point Challenge Course",
  },
  { label: "Action Point Mountain Bike", value: "Action Point Mountain Bike" },
  { label: "Action Point Skate Park", value: "Action Point Skate Park" },
  { label: "ALC Gate", value: "ALC Gate" },
  {
    label: "Aquatics/Water Reality (Goodrich west)",
    value: "Aquatics/Water Reality (Goodrich west)",
  },
  { label: "Arnold Logistic Center", value: "Arnold Logistic Center" },
  {
    label: "Arnold Logistic Center Bunker",
    value: "Arnold Logistic Center Bunker",
  },
  {
    label: "AT&T Stadium (non-program related)",
    value: "AT&T Stadium (non-program related)",
  },
  { label: "Base Camp A", value: "Base Camp A" },
  { label: "Base Camp B", value: "Base Camp B" },
  { label: "Base Camp C", value: "Base Camp C" },
  { label: "Base Camp D", value: "Base Camp D" },
  { label: "Base Camp E", value: "Base Camp E" },
  { label: "Base Camp F", value: "Base Camp F" },
  { label: "Bear Paw Trading Post", value: "Bear Paw Trading Post" },
  { label: "Big Zip Landing", value: "Big Zip Landing" },
  { label: "Big Zip Launch", value: "Big Zip Launch" },
  { label: "Boulder Cove", value: "Boulder Cove" },
  { label: "Brownsea Island", value: "Brownsea Island" },
  { label: "Buckskin Village", value: "Buckskin Village" },
  { label: "Celebrating Humanity", value: "Celebrating Humanity" },
  { label: "Charlie Dam", value: "Charlie Dam" },
  { label: "Conservation Trail", value: "Conservation Trail" },
  { label: "Consol Energy Bridge", value: "Consol Energy Bridge" },
  { label: "DEI Display", value: "DEI Display" },
  { label: "Delta Dam", value: "Delta Dam" },
  { label: "Digital Storytelling", value: "Digital Storytelling" },
  { label: "Eagles Nest Lodge", value: "Eagles Nest Lodge" },
  { label: "Facilities HQ", value: "Facilities HQ" },
  { label: "Family Adventure Camp", value: "Family Adventure Camp" },
  { label: "Fenneman Lodge/Perry Park", value: "Fenneman Lodge/Perry Park" },
  { label: "Fish Camp (Goodrich East)", value: "Fish Camp (Goodrich East)" },
  {
    label: "Fork in the Road Dining Hall",
    value: "Fork in the Road Dining Hall",
  },
  { label: "Freedom Field", value: "Freedom Field" },
  {
    label: "Garden Ground Mt Jack East Pavilion",
    value: "Garden Ground Mt Jack East Pavilion",
  },
  {
    label: "Garden Ground Mt Jack Outlook",
    value: "Garden Ground Mt Jack Outlook",
  },
  {
    label: "Garden Ground Mt Jack West Pavilion",
    value: "Garden Ground Mt Jack West Pavilion",
  },
  { label: "Health Lodge", value: "Health Lodge" },
  { label: "High Gear", value: "High Gear" },
  { label: "Highland Games", value: "Highland Games" },
  { label: "Hunters' Hall", value: "Hunters' Hall" },
  { label: "Jamboree Extravaganza", value: "Jamboree Extravaganza" },
  { label: "Jamboree Retail Food", value: "Jamboree Retail Food" },
  { label: "Jamboree Trading Post", value: "Jamboree Trading Post" },
  { label: "Jamboree Warrior", value: "Jamboree Warrior" },
  { label: "K-span", value: "K-span" },
  {
    label: "Leadership Heights - Outdoor",
    value: "Leadership Heights - Outdoor",
  },
  {
    label: "Legacy Village (Exhibitor Area)",
    value: "Legacy Village (Exhibitor Area)",
  },
  { label: "Low Gear Pavilion", value: "Low Gear Pavilion" },
  { label: "Maintenance Shop (Summit)", value: "Maintenance Shop (Summit)" },
  {
    label: "Marriott Leadership Lodge (Indoor)",
    value: "Marriott Leadership Lodge (Indoor)",
  },
  { label: "Medical - A/B Base Camp", value: "Medical - A/B Base Camp" },
  { label: "Medical - Charle Base Camp", value: "Medical - Charle Base Camp" },
  {
    label: "Medical - JHC/Mental Health/Specialty Clinic",
    value: "Medical - JHC/Mental Health/Specialty Clinic",
  },
  { label: "Medical - Summit Center", value: "Medical - Summit Center" },
  { label: "Medical - Thrasher Mtn", value: "medical_thrasher_mtn" },
  { label: "Medical HQ", value: "medical_hq" },
  { label: "Merit Badge Area", value: "merit_badge_area" },
  { label: "Military Display", value: "military_display" },
  { label: "Motor Pool (Jamboree)", value: "motor_pool_jamboree" },
  { label: "North Gate", value: "north_gate" },
  { label: "OA Summit Circle", value: "oa_summit_circle" },
  { label: "Other", value: "other" },
  { label: "Paintball", value: "paintball" },
  { label: "Performance Park", value: "performance_park" },
  { label: "Pigott Bunk House", value: "pigott_bunk_house" },
  { label: "Pigott Dining Hall", value: "pigott_dining_hall" },
  { label: "Pigott HQ", value: "pigott_hq" },
  { label: "Polaris ATV Pavilion", value: "polaris_atv_pavilion" },
  { label: "Presidential Helipad", value: "presidential_helipad" },
  { label: "Rock Borough", value: "rock_borough" },
  { label: "Scott Visitor Center", value: "scott_visitor_center" },
  { label: "South Gate", value: "south_gate" },
  { label: "Spartan Race", value: "spartan_race" },
  { label: "STEMQuest", value: "stemquest" },
  { label: "Summit Apex", value: "summit_apex" },
  { label: "Sustainability Treehouse", value: "sustainability_treehouse" },
  { label: "The Barrels", value: "the_barrels" },
  { label: "The Bows", value: "the_bows" },
  { label: "The Bunker Trading Post", value: "the_bunker_trading_post" },
  { label: "The Park", value: "the_park" },
  { label: "Thrasher Mountain - Other", value: "thrasher_mountain_other" },
  { label: "Tillerson Building", value: "tillerson_building" },
  {
    label: "Trail (List trail in incident description)",
    value: "trail_list_trail",
  },
  { label: "Wastewater Treatment", value: "wastewater_treatment" },
  { label: "WVU Display", value: "wvu_display" },
  {
    label: "Yamagata Lodge (residential)",
    value: "yamagata_lodge_residential",
  },
];

export const GCSList = [
  { label: "Please Select", value: "" },
  { label: "1", value: "1" },
  { label: "2", value: "2" },
  { label: "3", value: "3" },
  { label: "4", value: "4" },
  { label: "5", value: "5" },
  { label: "6", value: "6" },
  { label: "7", value: "7" },
  { label: "8", value: "8" },
  { label: "9", value: "9" },
  { label: "10", value: "10" },
  { label: "11", value: "11" },
  { label: "12", value: "12" },
  { label: "13", value: "13" },
  { label: "14", value: "14" },
  { label: "15", value: "15" },
];

export const I_GEL_SIZE_OPTION = [
  { label: "Size", value: "" },
  { label: "3", value: "3" },
  { label: "4", value: "4" },
  { label: "5", value: "5" },
];

export const I_GEL_ATTEMPTS_OPTION = [
  { label: "1", value: "1" },
  { label: "2", value: "2" },
  { label: "3", value: "3" },
];

export const DEVICES = [
  { label: "VL", value: "vl" },
  { label: "DL", value: "dl" },
  { label: "Digital", value: "digital" },
];

export const ATTEMPTS = [
  { label: "1", value: "1" },
  { label: "2", value: "2" },
  { label: "3", value: "3" },
  { label: "4", value: "4" },
  { label: "5", value: "5" },
];

export const TUBE_SIZE = [
  { label: "5.0", value: "5.0" },
  { label: "5.5", value: "5.5" },
  { label: "6.0", value: "6.0" },
  { label: "6.5", value: "6.5" },
  { label: "7.0", value: "7.0" },
  { label: "7.5", value: "7.5" },
  { label: "8.0", value: "8.0" },
];

export const BLADE_SIZE = [
  { label: "Mac 2", value: "Mac 2" },
  { label: "Mac 3", value: "Mac 3" },
  { label: "Mac 4", value: "Mac 4" },
  { label: "Miller", value: "Miller" },
];

export const IS_SUCCESSFUL = [
  {
    label: "Yes",
    value: "Yes",
  },
  {
    label: "No",
    value: "No",
  },
];

export const IV_INSERTION_SIZE = [
  { label: "Size", value: "" },
  { label: "14", value: "14" },
  { label: "16", value: "16" },
  { label: "18", value: "18" },
  { label: "20", value: "20" },
  { label: "22", value: "22" },
];

// export const MEDICATIONS_ADMINISTERED_LIST = [
//   { label: "Select", value: "" },
//   { label: "Albuterol", value: "albuterol" },
//   { label: "Aspirin", value: "aspirin" },
//   { label: "Atropine", value: "atropine" },
//   { label: "Dextrose 10%", value: "dextrose_10" },
//   { label: "Epinephrine", value: "epinephrine" },
//   { label: "Ipratropium", value: "ipratropium" },
//   { label: "Glucose (oral)", value: "glucose_oral" },
//   { label: "Lidocaine 2%", value: "lidocaine_2" },
//   { label: "Nitroglycerine", value: "nitroglycerine" },
//   { label: "Oxygen", value: "oxygen" },
//   { label: "Ondansetron", value: "ondansetron" },
//   { label: "Calcium Chloride", value: "calcium_chloride" },
//   { label: "Etomidate", value: "etomidate" },
//   { label: "Ketamine", value: "ketamine" },
//   { label: "Rocuronium", value: "rocuronium" },
//   { label: "Versed", value: "versed" },
//   { label: "Fentanyl", value: "fentanyl" },
//   { label: "Ibuprofen", value: "ibuprofen" },
//   { label: "Acetaminophen", value: "acetaminophen" },
//   { label: "Ketorolac", value: "Ketorolac" },
//   { label: "Benadryl", value: "Benadryl" },
//   { label: "Dexamethasone", value: "dexamethasone" },
// ];

export const MEDICATIONS_ADMINISTERED_LIST = [
  { label: "Select", value: "" },
  { label: "0.9% NaCl (Sodium Chloride)", value: "nacl_0_9" },
  { label: "3% NaCl (Sodium Chloride)", value: "nacl_3" },
  { label: "D10W (Dextrose 10% in Water)", value: "d10w" },
  { label: "Acetaminophen", value: "acetaminophen" },
  { label: "Adenosine", value: "adenosine" },
  { label: "Atropine", value: "atropine" },
  { label: "Albuterol Sulfate", value: "albuterol_sulfate" },
  { label: "Aspirin", value: "aspirin" },
  { label: "Cefazolin", value: "cefazolin" },
  { label: "Dexamethasone", value: "dexamethasone" },
  { label: "Diphenhydramine", value: "diphenhydramine" },
  { label: "Epinephrine", value: "epinephrine" },
  { label: "Ipratropium Bromide", value: "ipratropium_bromide" },
  { label: "Ketorolac", value: "ketorolac" },
  { label: "Naloxone", value: "naloxone" },
  { label: "Nitroglycerin", value: "nitroglycerin" },
  { label: "Ondansetron", value: "ondansetron" },
  { label: "Oral Glucose", value: "oral_glucose" },
  { label: "Ketamine", value: "ketamine" },
  { label: "Fentanyl", value: "fentanyl" },
  { label: "Midazolam", value: "midazolam" },
];

export const MA_ROUTE = [
  { label: "IV", value: "IV" },
  { label: "IM", value: "IM" },
  { label: "PO", value: "PO" },
  { label: "SQ", value: "SQ" },
];

export const MA_RESPONSE = [
  { label: "Improve", value: "Improve" },
  { label: "No change", value: "No change" },
  { label: "Worsen", value: "Wornsen" },
];

export const DEVICE = [
  { label: "Select", value: "" },
  { label: "NC", value: "NC" },
  { label: "NRB", value: "NRB" },
  { label: "Neb", value: "Neb" },
  { label: "BVM", value: "BVM" },
];

export const IMPRESSION_DIAGNOSIS = [
  { label: "Please Select", value: "" },
  { label: "Abdominal Pain", value: "abdominal_pain" },
  { label: "Abscess", value: "abscess" },
  { label: "Abrasion - Extremity", value: "abrasion_extremity" },
  { label: "Abrasion - Head", value: "abrasion_head" },
  { label: "Abrasion - Trunk", value: "abrasion_trunk" },
  { label: "Airway Obstruction", value: "airway_obstruction" },
  { label: "Allergic Reaction", value: "allergic_reaction" },
  { label: "Anxiety", value: "anxiety" },
  { label: "Asthma", value: "asthma" },
  { label: "Atrial Fibrillation - AFIB", value: "atrial_fibrillation_afib" },
  { label: "Avulsed Nail", value: "avulsed_nail" },
  { label: "Avulsed Tooth", value: "avulsed_tooth" },
  { label: "Back Pain", value: "back_pain" },
  { label: "Bleeding/Hemorrhage", value: "bleeding_hemorrhage" },
  { label: "Blister", value: "blister" },
  {
    label: "Blood in Stool (Hematochezia)",
    value: "blood_in_stool_hematochezia",
  },
  { label: "Blood in Urine (Hematuria)", value: "blood_in_urine_hematuria" },
  { label: "Blunt Trauma", value: "blunt_trauma" },
  { label: "Bruise, Contusion", value: "bruise_contusion" },
  {
    label: "Burn - First Degree (Sunburn) LOCATION",
    value: "burn_first_degree",
  },
  {
    label: "Burn - Second Degree (Blistering) LOCATION",
    value: "burn_second_degree",
  },
  {
    label: "Burn - Third Degree (Full-Thickness) LOCATION",
    value: "burn_third_degree",
  },
  { label: "Cardiac Arrest", value: "cardiac_arrest" },
  { label: "Cellulitis", value: "cellulitis" },
  { label: "Chest Pain", value: "chest_pain" },
  { label: "Choking", value: "choking" },
  { label: "Congestive Heart Failure", value: "congestive_heart_failure" },
  { label: "Constipation", value: "constipation" },
  { label: "Conjunctivitis", value: "conjunctivitis" },
  { label: "Corneal Abrasion", value: "corneal_abrasion" },
  { label: "Corneal Foreign Body", value: "corneal_foreign_body" },
  { label: "Cough", value: "cough" },
  { label: "Cramps (Colic)", value: "cramps_colic" },
  {
    label: "Dental Pain/Dental Complication",
    value: "dental_pain_complication",
  },
  { label: "Dehydration", value: "dehydration" },
  { label: "Depression", value: "depression" },
  { label: "Diabetic Complication", value: "diabetic_complication" },
  { label: "Diarrhea", value: "diarrhea" },
  { label: "Dizziness/Lightheadedness", value: "dizziness_lightheadedness" },
  { label: "Ingrown Toenail", value: "ingrown_toenail" },
  { label: "Insect/Bug Bite/Sting", value: "insect_bite_sting" },
  { label: "Joint Pain (Arthralgia)", value: "joint_pain_arthralgia" },
  { label: "Laceration - Extremity", value: "laceration_extremity" },
  { label: "Laceration - Head", value: "laceration_head" },
  { label: "Laceration - Trunk", value: "laceration_trunk" },
  {
    label: "Late Menstrual Period (Amenorrhea)",
    value: "late_menstrual_period",
  },
  { label: "Major Trauma", value: "major_trauma" },
  { label: "Malaise (General Illness)", value: "malaise_general_illness" },
  {
    label: "Menstrual Cramping, Dysmenorrhea",
    value: "menstrual_cramping_dysmenorrhea",
  },
  { label: "Myalgia (Muscle Pain)", value: "myalgia_muscle_pain" },
  { label: "Myocardial Infarction", value: "myocardial_infarction" },
  { label: "Nausea", value: "nausea" },
  { label: "Otitis Media", value: "otitis_media" },
  {
    label: "Otitis Externa (Swimmer's Ear)",
    value: "otitis_externa_swimmers_ear",
  },
  { label: "Painful Urination (Dysuria)", value: "painful_urination_dysuria" },
  { label: "Pain", value: "pain" },
  { label: "Palpitations", value: "palpitations" },
  { label: "Penetrating Trauma", value: "penetrating_trauma" },
  { label: "Respiratory Arrest", value: "respiratory_arrest" },
  { label: "Road Rash", value: "road_rash" },
  { label: "Seizure", value: "seizure" },
  { label: "Sinusitis", value: "sinusitis" },
  { label: "Sore Throat", value: "sore_throat" },
  { label: "Short of Breath", value: "short_of_breath" },
  { label: "Splinter", value: "splinter" },
  { label: "Sprain", value: "sprain" },
  { label: "Sting/Bite", value: "sting_bite" },
  { label: "Stroke (Suspected)", value: "stroke_suspected" },
  { label: "Strain", value: "strain" },
  { label: "Substance Abuse/OD", value: "substance_abuse_od" },
  { label: "Suture Removal", value: "suture_removal" },
  { label: "Swelling", value: "swelling" },
  { label: "Tachycardia", value: "tachycardia" },
  { label: "Testicle Pain", value: "testicle_pain" },
  { label: "Unconscious/Unresponsive", value: "unconscious_unresponsive" },
  { label: "Urinary Frequency", value: "urinary_frequency" },
  { label: "UTI", value: "uti" },
  { label: "Vaginal Discharge", value: "vaginal_discharge" },
  { label: "Vaginal Itching", value: "vaginal_itching" },
  { label: "Viral URI (Upper Respiratory Infection)", value: "viral_uri" },
  { label: "Vomiting", value: "vomiting" },
  { label: "Wound Care", value: "wound_care" },
  { label: "Other", value: "other" },
];

export const DISPOSITION_LIST = [
  { label: "Treated and Transported", value: "treated_and_transported" },
  { label: "Treated and Released", value: "treated_and_released" },
  {
    label: "Refusal of Medical Aid or Transport",
    value: "refusal_medical_aid_transport",
  },
  { label: "Sent to hospital by EMS", value: "sent_hospital_ems" },
  { label: "Sent to hospital by AIR", value: "sent_hospital_air" },
  { label: "Sent to hospital by POV", value: "sent_hospital_pov" },
  {
    label: "Sent to hospital by NSJ Transportation",
    value: "sent_hospital_nsj",
  },
];

export const TRANSFERRING_UNIT_LIST = [
  { label: "Please Select", value: "" },
  { label: "UTV 611", value: "utv_611" },
  { label: "UTV 612", value: "utv_612" },
  { label: "UTV 613", value: "utv_613" },
  { label: "UTV 614", value: "utv_614" },
  { label: "UTV 615", value: "utv_615" },
  { label: "MedBed 621", value: "medbed_621" },
  { label: "MedBed 622", value: "medbed_622" },
  { label: "MedBed 623", value: "medbed_623" },
  { label: "MedBed 624", value: "medbed_624" },
  { label: "MedBed 625", value: "medbed_625" },
  { label: "FLA 770", value: "fla_770" },
  { label: "FLA 771", value: "fla_771" },
  { label: "FLA 772", value: "fla_772" },
  { label: "FLA 773", value: "fla_773" },
  { label: "FLA 774", value: "fla_774" },
  { label: "FLA 775", value: "fla_775" },
  { label: "FLA 776", value: "fla_776" },
  { label: "FLA 777", value: "fla_777" },
  { label: "FLA 778", value: "fla_778" },
  { label: "FLA 779", value: "fla_779" },
  { label: "FLA 780", value: "fla_780" },
  { label: "FLA 781", value: "fla_781" },
  { label: "FLA 782", value: "fla_782" },
  { label: "FLA 783", value: "fla_783" },
  { label: "FLA 784", value: "fla_784" },
  { label: "FLA 785", value: "fla_785" },
  { label: "FLA 786", value: "fla_786" },
  { label: "FLA 787", value: "fla_787" },
  { label: "FLA 788", value: "fla_788" },
  { label: "FLA 789", value: "fla_789" },
  { label: "MD1", value: "md1" },
  { label: "MD2", value: "md2" },
  { label: "MD3", value: "md3" },
  { label: "MEDIC 631", value: "medic_631" },
  { label: "MEDIC 632", value: "medic_632" },
  { label: "MEDIC 633", value: "medic_633" },
  { label: "BIKE 651", value: "bike_651" },
  { label: "BIKE 652", value: "bike_652" },
  { label: "BIKE 653", value: "bike_653" },
  { label: "BIKE 654", value: "bike_654" },
  { label: "601", value: "601" },
  { label: "602", value: "602" },
  { label: "603", value: "603" },
  { label: "604", value: "604" },
  { label: "605", value: "605" },
  { label: "606", value: "606" },
];

export const TRANSPORT_DESTINATION_LIST = [
  { label: "Jamboree Health Center", value: "jamboree_health_center" },
  {
    label: "Summit Center Medical Center",
    value: "summit_center_medical_center",
  },
  {
    label: "Charlie Base Camp Medical Center",
    value: "charlie_base_camp_medical_center",
  },
  {
    label: "Alpha/Bravo Base Camp Medical Center",
    value: "alpha_bravo_base_camp_medical_center",
  },
  { label: "Thrasher Mtn Medical", value: "thrasher_mtn_medical" },
  {
    label: "Jamboree Mental Health Clinic",
    value: "jamboree_mental_health_clinic",
  },
  { label: "Summit Health Lodge", value: "summit_health_lodge" },
];

export const HOSPITAL_OFF_SITE_FACILITY = [
  { label: "Please Select", value: "" },
  { label: "Raleigh General Hospital", value: "raleigh_general_hospital" },
  {
    label: "Beckley Appalachian Regional Hospital",
    value: "beckley_appalachian_regional_hospital",
  },
  { label: "CAMC Plateau", value: "camc_plateau" },
  { label: "CAMC General", value: "camc_general" },
  {
    label: "Carillon Roanoke Community Hospital",
    value: "carillon_roanoke_community_hospital",
  },
  { label: "Med Express - Fayetteville", value: "med_express_fayetteville" },
  { label: "Med Express - Beckley", value: "med_express_beckley" },
  { label: "Other", value: "other" },
];

export const HOSPITAL_OFF_SITE_FACILITY_TRANSFERRING_UNIT = [
  { label: "Please Select", value: "" },
  { label: "JanCare", value: "jancare" },
  { label: "MHFEMS", value: "mhfems" },
  { label: "Other EMS", value: "other_ems" },
  { label: "Jamboree Transportation", value: "sbr_transport" },
  { label: "POV", value: "pov" },
  { label: "HealthNet", value: "healthnet" },
  { label: "AirEvac Life Team", value: "airevac_life_team" },
];

export const FINAL_PRIMARY_TREATING_CLINICIAN = [
  { label: "Please Select", value: "" },
  { label: "Dunn, DO, Erica", value: "dunn_erica" },
  { label: "Kupas, MD, Douglas", value: "kupas_douglas" },
  { label: "Wilson, MD, Bryan", value: "wilson_bryan" },
  { label: "Bates, Matthew", value: "bates_matthew" },
  { label: "Bennatts, Timothy", value: "bennatts_timothy" },
  { label: "Bice, Jeremy", value: "bice_jeremy" },
  { label: "Blatman, Jonathan", value: "blatman_jonathan" },
  { label: "Clayton, Sean", value: "clayton_sean" },
  { label: "Coe, Spencer", value: "coe_spencer" },
  { label: "Dale, Denise", value: "dale_denise" },
  { label: "Drisko, Shelley", value: "drisko_shelley" },
  { label: "Eggum, James", value: "eggum_james" },
  { label: "Eggum, John", value: "eggum_john" },
  { label: "Engbrecht, Calvin", value: "engbrecht_calvin" },
  { label: "Fager, Robert", value: "fager_robert" },
  { label: "Frankiewicz, Nicholas", value: "frankiewicz_nicholas" },
  { label: "Gehin, Bradley", value: "gehin_bradley" },
  { label: "Geishecker, Edward", value: "geishecker_edward" },
  { label: "Harbaugh, John", value: "harbaugh_john" },
  { label: "Hebert, Darin", value: "hebert_darin" },
  { label: "Hoffmann, Jakob", value: "hoffmann_jakob" },
  { label: "Hood, James", value: "hood_james" },
  { label: "Hunter, Howard", value: "hunter_howard" },
  { label: "Jasper, Jordan", value: "jasper_jordan" },
  { label: "Kane, Benjamin", value: "kane_benjamin" },
  { label: "Kane, Daniel", value: "kane_daniel" },
  { label: "Knight, Payne", value: "knight_payne" },
  { label: "Knoff, Richard", value: "knoff_richard" },
  { label: "Lanman, Thomas (Tim)", value: "lanman_thomas_tim" },
  { label: "Lanman, Tom", value: "lanman_tom" },
  { label: "Laronuis, James", value: "laronuis_james" },
  { label: "Leff, Gregory", value: "leff_gregory" },
  { label: "Leff, Nathan", value: "leff_nathan" },
  { label: "Little, Stuart", value: "little_stuart" },
  { label: "Longworth, Rebecca", value: "longworth_rebecca" },
  { label: "Lubenetski, Christopher", value: "lubenetski_christopher" },
  { label: "Noyes, Christopher", value: "noyes_christopher" },
  { label: "Rehboch, John", value: "rehboch_john" },
  { label: "Richards, Michael", value: "richards_michael" },
  { label: "Roberts, John", value: "roberts_john" },
  { label: "Rogers, Donald", value: "rogers_donald" },
  { label: "Sarto, Patrick", value: "sarto_patrick" },
  { label: "Sebo, Shawn", value: "sebo_shawn" },
  { label: "Smith, Andrew", value: "smith_andrew" },
  { label: "Stulken, Carter", value: "stulken_carter" },
  { label: "Stutzmann, Aidan", value: "stutzmann_aidan" },
  { label: "Tashner, John", value: "tashner_john" },
  { label: "Taylor, Merritt", value: "taylor_merritt" },
  { label: "Todd, David", value: "todd_david" },
  { label: "Turner, Michael", value: "turner_michael" },
  { label: "Vanderhans, Christopher", value: "vanderhans_christopher" },
  { label: "Watring, Dalton", value: "watring_dalton" },
  { label: "Winsor, Erin", value: "winsor_erin" },
  { label: "Zemienieuski, Timothy", value: "zemienieuski_timothy" },
  { label: "**Military FLA Placeholder**", value: "military_fla_placeholder" },
];
